// field types
import React from 'react';
import faker from 'faker';
import * as Yup from 'yup';
import moment from 'moment';

import { FieldType, WidgetType } from 'src/types';
import TextRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/TextRenderer';
import DateRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/DateRenderer';
import DateTimeRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/DateTimeRenderer';
import BooleanRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/BooleanRenderer';
import CurrencyRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/CurrencyRenderer';
import DecimalRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/DecimalRenderer';
import EmailRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/EmailRenderer';
import UrlRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/UrlRenderer';
import SelectRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/SelectRenderer';
import FileAttachmentRenderer from '../shared/Perspectives/Renderers/TableRenderer/ColumnRenderer/FileAttachmentRenderer';

// Field types
export const TEXT = 'TEXT';
export const DATE = 'DATE';
export const DATETIME = 'DATETIME';
export const CHECKBOX = 'CHECKBOX';
export const CURRENCY = 'CURRENCY';
export const NUMBER = 'NUMBER';
export const EMAIL = 'EMAIL';
export const URL = 'URL';
export const SELECT = 'SELECT';
export const RADIO = 'RADIO';
export const FILE_ATTACHMENT = 'FILE_ATTACHMENT';

// Form widgets - all field types have form widgets, not every form widget is a field type
export const TEXT_FIELD = 'TEXT_FIELD';
export const DATE_FIELD = 'DATE_FIELD';
export const DATETIME_FIELD = 'DATETIME_FIELD';
export const CHECKBOX_FIELD = 'CHECKBOX_FIELD';
export const CURRENCY_FIELD = 'CURRENCY_FIELD';
export const NUMBER_FIELD = 'NUMBER_FIELD';
export const EMAIL_FIELD = 'EMAIL_FIELD';
export const URL_FIELD = 'URL_FIELD';
export const RADIO_FIELD = 'RADIO_FIELD';
export const SELECT_FIELD = 'SELECT_FIELD';
export const FILE_ATTACHMENT_FIELD = 'FILE_ATTACHMENT_FIELD';

export const ROLE_ASSIGNMENTS_FIELD = 'ROLE_ASSIGNMENTS_FIELD';

interface FieldDetails {
  columnRenderer: React.ComponentType<any>,
  generateFakeData: () => any,
  label: string,
  validator: Object,
  widgetType: WidgetType,
  icon: string,
}

// TODO - add long text field
export const FIELD_DETAILS_BY_FIELD_TYPE: Record<FieldType, FieldDetails> = {
  [TEXT]: {
    columnRenderer: TextRenderer,
    generateFakeData: () => faker.lorem.word(),
    label: 'Text',
    validator: Yup.string().required('Required'),
    widgetType: TEXT_FIELD,
    icon: 'type',
  },
  [DATE]: {
    columnRenderer: DateRenderer,
    generateFakeData: () => faker.date.between(moment().subtract(10, 'days').format(), moment().add(10, 'days').format()),
    label: 'Date',
    validator: Yup.string().required('Required'),
    widgetType: DATE_FIELD,
    icon: 'calendar',
  },
  [DATETIME]: {
    columnRenderer: DateTimeRenderer,
    generateFakeData: () => faker.date.between(moment().subtract(10, 'days').format(), moment().add(10, 'days').format()),
    label: 'Date & time',
    validator: Yup.string().required('Required'),
    widgetType: DATETIME_FIELD,
    icon: 'clock',
  },
  [CHECKBOX]: {
    columnRenderer: BooleanRenderer,
    generateFakeData: () => faker.random.boolean(),
    label: 'Checkbox',
    validator: Yup.string().required('Required'),
    widgetType: CHECKBOX_FIELD,
    icon: 'check-square',
  },
  [RADIO]: {
    columnRenderer: BooleanRenderer,
    generateFakeData: () => faker.random.boolean(),
    label: 'Radio',
    validator: Yup.string().required('Required'),
    widgetType: RADIO_FIELD,
    icon: 'list',
  },
  [CURRENCY]: {
    columnRenderer: CurrencyRenderer,
    generateFakeData: () => faker.finance.amount(),
    label: 'Currency',
    validator: Yup.string().required('Required'),
    widgetType: CURRENCY_FIELD,
    icon: 'dollar-sign',
  },
  [NUMBER]: { // no decimal vs int - let user set rounding
    columnRenderer: DecimalRenderer,
    generateFakeData: () => faker.random.float(),
    label: 'Number',
    validator: Yup.string().required('Required'),
    widgetType: NUMBER_FIELD,
    icon: 'hash',
  },
  [EMAIL]: {
    columnRenderer: EmailRenderer,
    generateFakeData: () => faker.internet.email(),
    label: 'Email',
    validator: Yup.string().required('Required'),
    widgetType: EMAIL_FIELD,
    icon: 'at-sign',
  },
  [URL]: {
    columnRenderer: UrlRenderer,
    generateFakeData: () => faker.internet.url(),
    label: 'URL',
    validator: Yup.string().required('Required'),
    widgetType: URL_FIELD,
    icon: 'link',
  },
  [SELECT]: {
    columnRenderer: SelectRenderer,
    generateFakeData: () => faker.lorem.word(),
    label: 'Select',
    validator: Yup.string().required('Required'),
    widgetType: SELECT_FIELD,
    icon: 'list',
  },
  [FILE_ATTACHMENT]: {
    columnRenderer: FileAttachmentRenderer,
    generateFakeData: () => faker.lorem.word(),
    label: 'File attachment',
    validator: Yup.string().required('Required'),
    widgetType: FILE_ATTACHMENT_FIELD,
    icon: 'file-text',
  },
};
export const FIELD_OPTIONS = Object.keys(FIELD_DETAILS_BY_FIELD_TYPE).map((field) => ({
  // @ts-ignore
  label: FIELD_DETAILS_BY_FIELD_TYPE[field].label,
  value: field,
}));
